import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Shirt, ShoppingCart, Loader2, XCircle, Package, ArrowRight, Trash2, ZoomIn, X, ChevronLeft, ChevronRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Carousel images of people wearing the merch
const carouselImages = [
  { src: "/products/carousel_1.jpeg", alt: "Youth in Calvin Butts apparel" },
  { src: "/products/carousel_2.jpeg", alt: "CB 1982 Collection" },
  { src: "/products/carousel_3.jpeg", alt: "CB 82 Red Collection" },
  { src: "/products/carousel_4.jpeg", alt: "Calvin Butts Signature Collection" },
  { src: "/products/carousel_5.png", alt: "How's Your Mental collection" }
];

function Shop() {
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedSizes, setSelectedSizes] = useState({});
  const [selectedColors, setSelectedColors] = useState({});
  const [zoomedImage, setZoomedImage] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);
  };

  const products = [
    // NEW: LIVE X CB Collaboration (Featured/Pre-Order)
    {
      id: "live-cb-collab",
      name: "LIVE X CB Collaboration Tee",
      price: 49.95,
      description: "Exclusive collaboration featuring CB heart logo on front and L.I.V.E. (Legacy, Investment, Value, Equity) on the back. Premium heavyweight cotton.",
      image: "/products/live_cb_collab.webp",
      colorImages: {
        "Cream": { front: "/products/live_cb_collab.webp", back: "/products/live_cb_model.png" },
        "Black": { front: "/products/live_cb_black_flat.png", back: "/products/live_cb_black_model.png" }
      },
      sizes: ["S", "M", "L", "XL", "XXL"],
      category: "Limited Edition",
      colors: ["Cream", "Black"],
      featured: true
    },
    // How's Your Mental? Tan Crewneck ($39.95)
    {
      id: "hows-your-mental-tan",
      name: "How's Your Mental? Crewneck - Tan",
      price: 39.95,
      description: "Cozy tan crewneck sweatshirt featuring the CB heart logo on front and 'How's your mental?' message on back.",
      image: "/products/tan_crewneck_front.png",
      imageBack: "/products/tan_crewneck_back.png",
      sizes: ["S", "M", "L", "XL", "XXL"],
      category: "Sweatshirts",
      colors: ["Tan"]
    },
    // How's Your Mental? Grey Hoodie (Red logo) - $39.95
    {
      id: "hows-your-mental-grey",
      name: "How's Your Mental? Hoodie - Grey (Red)",
      price: 39.95,
      description: "Premium grey hoodie featuring the red CB heart logo on front and red 'How's your mental?' message on back. Heavyweight fleece with hood.",
      image: "/products/mental_grey_red_front.jpg",
      imageBack: "/products/mental_grey_red_back.jpg",
      sizes: ["S", "M", "L", "XL", "XXL"],
      category: "Hoodies",
      colors: ["Grey"]
    },
    // NEW: T-Shirts ($35)
    {
      id: "cdb-tshirt-classic",
      name: "How's Your Mental? T-Shirt - Blue",
      price: 35.00,
      description: "Comfortable everyday tee with CB heart logo on front and 'How's your mental?' message on back. Made from 100% premium cotton for all-day wear.",
      image: "/products/tshirt_blue_front_white.png",
      imageBack: "/products/tshirt_blue_back_white.png",
      sizes: ["S", "M", "L", "XL", "XXL"],
      category: "T-Shirts",
      colors: ["Blue"]
    },
    // NEW: Hoodie ($50)
    {
      id: "cdb-hoodie-signature",
      name: "How's Your Mental? Hoodie",
      price: 39.95,
      description: "Classic heather grey hoodie featuring the white CB heart logo on front and 'How's your mental?' message on back. Soft fleece with drawstring hood and kangaroo pocket.",
      image: "/products/hoodie_grey_cb_white_bg.png",
      imageBack: "/products/hoodie_grey_back_white.png",
      sizes: ["S", "M", "L", "XL", "XXL"],
      category: "Hoodies",
      colors: ["Grey"]
    },
    // NEW: Sweatsuit ($69.99 Sale) - Grey
    {
      id: "cdb-sweatsuit-set",
      name: "1982 Calvin Butts Sweatsuit Set - Grey",
      price: 69.99,
      originalPrice: 79.99,
      description: "Full sweatsuit set includes matching '1982 Calvin Butts' hoodie and '82' joggers. Perfect for lounging or showing your support in style!",
      image: "/products/sweatsuit_1982.png",
      sizes: ["S", "M", "L", "XL", "XXL"],
      category: "Sets",
      colors: ["Grey"],
      onSale: true
    },
    // NEW: Sweatsuit ($69.99 Sale) - Black
    {
      id: "cdb-sweatsuit-set-black",
      name: "1982 Calvin Butts Sweatsuit Set - Black",
      price: 69.99,
      originalPrice: 79.99,
      description: "Full sweatsuit set in black with red '1982 Calvin Butts' logo hoodie and '82' joggers. Perfect for lounging or showing your support in style!",
      image: "/products/sweatsuit_black_1982.png",
      sizes: ["S", "M", "L", "XL", "XXL"],
      category: "Sets",
      colors: ["Black"],
      onSale: true
    },
    // NEW: 1982 Calvin Butts Hoodie - Black
    {
      id: "hoodie-1982-black",
      name: "1982 Calvin Butts Hoodie - Black",
      price: 50.00,
      description: "Premium black hoodie with red '1982 Calvin Butts' logo. Heavyweight fleece with kangaroo pocket and drawstring hood.",
      image: "/products/hoodie_1982_black_white_bg.png",
      sizes: ["S", "M", "L", "XL", "XXL"],
      category: "Hoodies",
      colors: ["Black"]
    },
    // How's Your Mental? Sweatsuit Set - White
    {
      id: "mental-sweatsuit-white",
      name: "How's Your Mental? Sweatsuit Set - White",
      price: 69.99,
      originalPrice: 79.99,
      description: "Full sweatsuit set in white/cream with yellow 'How's your mental?' hoodie and matching CB heart logo joggers. Perfect for lounging in style!",
      image: "/products/white_mental_sweatsuit.png",
      sizes: ["S", "M", "L", "XL", "XXL"],
      category: "Sets",
      colors: ["White"],
      onSale: true
    },
    // CB Heart Logo Joggers - Grey
    {
      id: "cb-joggers-grey",
      name: "CB Heart Logo Joggers - Grey",
      price: 45.00,
      description: "Comfortable grey joggers featuring the CB heart logo. Soft fleece with elastic waistband and cuffed ankles.",
      image: "/products/grey_joggers_cb.png",
      sizes: ["S", "M", "L", "XL", "XXL"],
      category: "Bottoms",
      colors: ["Grey"]
    },
    {
      id: "cdb-leggings",
      name: "CDB Athletic Leggings",
      price: 35.00,
      description: "Premium compression leggings with CDB heart logo",
      image: "/products/product3.png",
      sizes: ["XS", "S", "M", "L", "XL", "2XL"],
      category: "Activewear",
      colors: ["Black"]
    },
    {
      id: "mental-health-bucket-hat",
      name: "Mental Health Awareness Bucket Hat",
      price: 25.00,
      description: "Stylish denim bucket hat with 'How's your mental?' message",
      image: "/products/product2.png",
      sizes: ["One Size"],
      category: "Accessories",
      colors: ["Denim"]
    },
    {
      id: "cdb-shorts",
      name: "CDB Athletic Shorts",
      price: 30.00,
      description: "Comfortable athletic shorts with CDB heart logo",
      image: "/products/product1.png",
      sizes: ["XS", "S", "M", "L", "XL", "2XL"],
      category: "Activewear",
      colors: ["Black"]
    },
    {
      id: "grey-shirt-sale",
      name: "Grey CBMS T-Shirt",
      price: 14.99,
      originalPrice: 30.00,
      description: "Classic grey t-shirt with CBMS branding - ON SALE!",
      image: "/products/grey_shirt.webp",
      sizes: ["XS", "S", "M", "L", "XL", "2XL"],
      category: "T-Shirts",
      colors: ["Grey", "Yellow"],
      onSale: true
    },
    {
      id: "white-shirt-sale",
      name: "White OG T-Shirt",
      price: 14.99,
      originalPrice: 30.00,
      description: "Classic white t-shirt with OG branding - ON SALE!",
      image: "/products/white_shirt.webp",
      sizes: ["XS", "S", "M", "L", "XL", "2XL"],
      category: "T-Shirts",
      colors: ["White", "Green"],
      onSale: true
    }
  ];

  const handleSizeChange = (productId, size) => {
    setSelectedSizes({
      ...selectedSizes,
      [productId]: size
    });
  };

  const handleColorChange = (productId, color) => {
    setSelectedColors({
      ...selectedColors,
      [productId]: color
    });
  };

  const addToCart = (product) => {
    const size = selectedSizes[product.id];
    const color = selectedColors[product.id];
    
    if (!size) {
      setErrorMessage(`Please select a size for ${product.name}`);
      setTimeout(() => setErrorMessage(""), 3000);
      return;
    }

    // Only check color if product has multiple colors
    if (product.colors && product.colors.length > 1 && !color) {
      setErrorMessage(`Please select a color for ${product.name}`);
      setTimeout(() => setErrorMessage(""), 3000);
      return;
    }

    const cartItem = {
      ...product,
      size: size,
      color: color || product.colors[0],
      cartId: `${product.id}-${size}-${color || product.colors[0]}-${Date.now()}`
    };

    // Add to cart
    setCart([...cart, cartItem]);
    setErrorMessage("");
    
    // Reset selections for this product
    const newSizes = { ...selectedSizes };
    const newColors = { ...selectedColors };
    delete newSizes[product.id];
    delete newColors[product.id];
    setSelectedSizes(newSizes);
    setSelectedColors(newColors);

    // Scroll to cart
    setTimeout(() => {
      const cartElement = document.getElementById('shopping-cart');
      if (cartElement) {
        cartElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const removeFromCart = (cartId) => {
    setCart(cart.filter(item => item.cartId !== cartId));
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + item.price, 0).toFixed(2);
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      setErrorMessage("Your cart is empty");
      return;
    }

    // Navigate to checkout page with cart items
    navigate('/checkout', { state: { cart: cart } });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Carousel */}
      <section className="pt-24 relative overflow-hidden bg-black">
        <div className="relative h-[500px] md:h-[600px]">
          {/* Carousel Images */}
          {carouselImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-full object-contain"
              />
            </div>
          ))}

          {/* Overlay with Title */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex items-end justify-center pb-16">
            <div className="text-center text-white">
              <Badge className="bg-[#FFE102] text-[#17181B] font-bold mb-4">New Collection</Badge>
              <h1 className="text-4xl md:text-6xl font-bold mb-4">CDB Apparel Shop</h1>
              <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto px-4">
                Premium activewear and accessories. 100% of proceeds support our youth programs!
              </p>
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center transition-all backdrop-blur-sm"
            data-testid="carousel-prev"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center transition-all backdrop-blur-sm"
            data-testid="carousel-next"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>

          {/* Dots Indicator */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {carouselImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  index === currentSlide ? 'bg-[#FFE102] w-8' : 'bg-white/50 hover:bg-white/80'
                }`}
                data-testid={`carousel-dot-${index}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Error Messages */}
      {errorMessage && (
        <div className="max-w-7xl mx-auto px-4 mt-4">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <XCircle className="w-5 h-5 text-red-600 mr-3" />
            <span className="text-red-800">{errorMessage}</span>
          </div>
        </div>
      )}

      {/* Shopping Cart Summary Bar */}
      {cart.length > 0 && (
        <div className="sticky top-20 z-40 bg-[#1167B1] text-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <ShoppingCart className="w-6 h-6" />
                <span className="font-bold text-lg">
                  {cart.length} {cart.length === 1 ? 'item' : 'items'} in cart
                </span>
                <span className="text-[#FFE102] font-bold text-xl">
                  ${getTotalPrice()}
                </span>
              </div>
              <Button 
                onClick={handleCheckout}
                disabled={isProcessing}
                className="bg-[#FFE102] text-[#17181B] hover:bg-[#FFE102]/90 font-bold px-8"
                data-testid="quick-checkout-button"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  'Checkout Now'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Products Grid */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => {
              // Get current color or default to first color
              const currentColor = selectedColors[product.id] || (product.colors && product.colors[0]);
              // Get images based on selected color (if colorImages exists)
              const frontImage = product.colorImages && currentColor 
                ? product.colorImages[currentColor]?.front || product.image 
                : product.image;
              const backImage = product.colorImages && currentColor 
                ? product.colorImages[currentColor]?.back || product.imageBack 
                : product.imageBack;
              
              return (
              <Card key={product.id} className="border-2 hover:border-[#1167B1] transition-all hover:shadow-xl" data-testid={`product-${product.id}`}>
                <div className="aspect-square bg-white flex items-center justify-center overflow-hidden relative group cursor-pointer" onClick={() => setZoomedImage(frontImage)}>
                  {/* Front Image */}
                  <img 
                    src={frontImage} 
                    alt={product.name}
                    className={`w-full h-full object-cover absolute inset-0 transition-opacity duration-500 ${backImage ? 'group-hover:opacity-0' : ''}`}
                  />
                  {/* Back Image (if exists) - shows on hover */}
                  {backImage && (
                    <img 
                      src={backImage} 
                      alt={`${product.name} - Back`}
                      className="w-full h-full object-cover absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    />
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center z-10">
                    <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  {/* Front/Back indicator for products with both views */}
                  {backImage && (
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10 flex gap-1">
                      <span className="text-xs bg-black/60 text-white px-2 py-1 rounded group-hover:opacity-0 transition-opacity">Front</span>
                      <span className="text-xs bg-black/60 text-white px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity absolute left-1/2 -translate-x-1/2">Back</span>
                    </div>
                  )}
                </div>
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex gap-2 flex-wrap">
                      <Badge className="bg-[#FFE102] text-[#17181B]">{product.category}</Badge>
                      {product.featured && (
                        <Badge className="bg-[#1167B1] text-white">FEATURED</Badge>
                      )}
                      {product.onSale && (
                        <Badge className="bg-red-600 text-white">SALE</Badge>
                      )}
                    </div>
                    <div className="text-right">
                      <span className="text-2xl font-bold text-[#1167B1]">${product.price.toFixed(2)}</span>
                      {product.originalPrice && (
                        <div className="text-sm text-gray-500 line-through">${product.originalPrice.toFixed(2)}</div>
                      )}
                    </div>
                  </div>
                  <CardTitle className="text-xl text-[#17181B]">{product.name}</CardTitle>
                  <p className="text-gray-600 text-sm">{product.description}</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Color Selection - Only show if multiple colors */}
                  {product.colors && product.colors.length > 1 && (
                    <div>
                      <Label className="text-sm font-semibold mb-2 block">Select Color</Label>
                      <div className="flex flex-wrap gap-2">
                        {product.colors.map((color) => (
                          <button
                            key={color}
                            onClick={() => handleColorChange(product.id, color)}
                            className={`px-4 py-2 border-2 rounded-lg font-semibold transition-all text-sm ${
                              selectedColors[product.id] === color
                                ? 'border-[#1167B1] bg-[#1167B1] text-white'
                                : 'border-gray-300 hover:border-[#1167B1]'
                            }`}
                            data-testid={`color-${product.id}-${color}`}
                          >
                            {color}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Size Selection */}
                  <div>
                    <Label className="text-sm font-semibold mb-2 block">Select Size</Label>
                    <div className="flex flex-wrap gap-2">
                      {product.sizes.map((size) => (
                        <button
                          key={size}
                          onClick={() => handleSizeChange(product.id, size)}
                          className={`px-4 py-2 border-2 rounded-lg font-semibold transition-all ${
                            selectedSizes[product.id] === size
                              ? 'border-[#1167B1] bg-[#1167B1] text-white'
                              : 'border-gray-300 hover:border-[#1167B1]'
                          }`}
                          data-testid={`size-${product.id}-${size}`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Add to Cart Button */}
                  <Button 
                    onClick={() => addToCart(product)}
                    className="w-full bg-[#FFE102] text-[#17181B] hover:bg-[#FFE102]/90 font-bold"
                    data-testid={`add-to-cart-${product.id}`}
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Add to Cart
                  </Button>
                </CardContent>
              </Card>
            );
            })}
          </div>
        </div>
      </section>

      {/* Cart Details Section */}
      {cart.length > 0 && (
        <section id="shopping-cart" className="py-12 px-4 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-[#1167B1] shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl text-[#17181B] flex items-center">
                  <Package className="w-6 h-6 mr-2" />
                  Your Shopping Cart
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {cart.map((item) => (
                    <div key={item.cartId} className="flex items-center justify-between p-4 bg-white rounded-lg border hover:border-[#1167B1] transition-all">
                      <div className="flex items-center space-x-4 flex-1">
                        <img 
                          src={item.image} 
                          alt={item.name} 
                          className="w-20 h-20 object-cover rounded"
                        />
                        <div className="flex-1">
                          <p className="font-bold text-[#17181B]">{item.name}</p>
                          <p className="text-sm text-gray-600">
                            Size: <span className="font-semibold">{item.size}</span>
                            {item.color && item.colors && item.colors.length > 1 && (
                              <> • Color: <span className="font-semibold">{item.color}</span></>
                            )}
                          </p>
                          <p className="text-sm text-gray-500">{item.category}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <span className="font-bold text-[#1167B1] text-lg">${item.price.toFixed(2)}</span>
                        <button
                          onClick={() => removeFromCart(item.cartId)}
                          className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded transition-all"
                          title="Remove item"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}

                  <div className="border-t pt-4 mt-4">
                    <div className="flex justify-between items-center mb-6">
                      <span className="text-xl font-bold text-[#17181B]">Total:</span>
                      <span className="text-3xl font-bold text-[#1167B1]">${getTotalPrice()}</span>
                    </div>

                    <Button 
                      onClick={handleCheckout}
                      disabled={isProcessing}
                      className="w-full bg-[#1167B1] text-white hover:bg-[#0d5390] font-bold text-lg py-6"
                      data-testid="checkout-button"
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin mr-2" />
                          Processing Checkout...
                        </>
                      ) : (
                        <>
                          Proceed to Checkout <ArrowRight className="ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Info Section */}
      <section className="py-20 px-4 bg-[#1167B1]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 text-center text-white">
            <div>
              <div className="w-16 h-16 bg-[#FFE102] rounded-full flex items-center justify-center mx-auto mb-4">
                <Shirt className="w-8 h-8 text-[#17181B]" />
              </div>
              <h3 className="text-xl font-bold mb-2">Premium Quality</h3>
              <p className="text-white/80">High-quality materials and comfortable fit</p>
            </div>
            <div>
              <div className="w-16 h-16 bg-[#FFE102] rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="w-8 h-8 text-[#17181B]" />
              </div>
              <h3 className="text-xl font-bold mb-2">Fast Shipping</h3>
              <p className="text-white/80">Quick processing and delivery to your door</p>
            </div>
            <div>
              <div className="w-16 h-16 bg-[#FFE102] rounded-full flex items-center justify-center mx-auto mb-4">
                <ShoppingCart className="w-8 h-8 text-[#17181B]" />
              </div>
              <h3 className="text-xl font-bold mb-2">Support Our Mission</h3>
              <p className="text-white/80">100% of proceeds fund youth programs</p>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-xl text-white/90 mb-4">
              Every purchase directly supports STEAM education, mental health services, and career development for Metro Atlanta youth.
            </p>
            <p className="text-white/70">
              Thank you for making a difference in our community!
            </p>
          </div>
        </div>
      </section>

      {/* Image Zoom Modal */}
      {zoomedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setZoomedImage(null)}
          data-testid="image-zoom-modal"
        >
          <button
            className="absolute top-4 right-4 z-10 w-12 h-12 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-all"
            onClick={() => setZoomedImage(null)}
          >
            <X className="w-6 h-6 text-gray-800" />
          </button>
          <img 
            src={zoomedImage} 
            alt="Product zoom view"
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

export default Shop;
